import { useState, useMemo } from 'react'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateSurfaceArea } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

const TYPES = [
  { value: 'masonry', label: 'Masonry / Blockwork' }, { value: 'plaster', label: 'Plaster / Rendering' },
  { value: 'waterproofing', label: 'Waterproofing' }, { value: 'flooring', label: 'Flooring / Tiling' },
  { value: 'painting', label: 'Painting' }, { value: 'ceiling', label: 'Ceiling' },
]

export default function SurfaceAreaModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:surface_area:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { surfaces: [], wastePct: '' })
  const initial = superseding ? { surfaces: superseding.engineering_inputs?.surfaces || [], wastePct: superseding.engineering_inputs?.wastePct ?? '' } : draft

  const [surfaces, setSurfacesRaw] = useState(initial.surfaces)
  const [wastePct, setWastePctRaw] = useState(initial.wastePct)
  const setSurfaces = (u) => setSurfacesRaw((p) => { const n = typeof u === 'function' ? u(p) : u; if (!superseding) setDraft((d) => ({ ...d, surfaces: n })); return n })
  const setWastePct = (v) => { setWastePctRaw(v); if (!superseding) setDraft((d) => ({ ...d, wastePct: v })) }

  function addSurface() { setSurfaces((p) => [...p, { id: `S${Date.now()}`, label: '', type: 'masonry', useArea: false, area: '', length: '', height: '', upstandHeight: '', numberOfCoats: '', openings: [] }]) }
  function updateSurface(id, patch) { setSurfaces((p) => p.map((s) => (s.id === id ? { ...s, ...patch } : s))) }
  function removeSurface(id) { setSurfaces((p) => p.filter((s) => s.id !== id)) }
  function addOpening(sid) { setSurfaces((p) => p.map((s) => (s.id === sid ? { ...s, openings: [...s.openings, { id: `O${Date.now()}`, label: '', length: '', height: '' }] } : s))) }
  function updateOpening(sid, oid, patch) { setSurfaces((p) => p.map((s) => (s.id === sid ? { ...s, openings: s.openings.map((o) => (o.id === oid ? { ...o, ...patch } : o)) } : s))) }
  function removeOpening(sid, oid) { setSurfaces((p) => p.map((s) => (s.id === sid ? { ...s, openings: s.openings.filter((o) => o.id !== oid) } : s))) }

  const surfacesForCalc = useMemo(() => surfaces.map((s) => ({
    label: s.label, type: s.type,
    area: s.useArea ? (s.area === '' ? NaN : Number(s.area)) : null,
    length: s.useArea ? undefined : (s.length === '' ? NaN : Number(s.length)),
    height: s.useArea ? undefined : (s.height === '' ? NaN : Number(s.height)),
    upstandHeight: s.upstandHeight === '' ? undefined : Number(s.upstandHeight),
    systemSpecification: s.systemSpecification || undefined,
    numberOfCoats: s.numberOfCoats === '' ? undefined : Number(s.numberOfCoats),
    openings: s.openings.map((o) => ({ label: o.label, length: Number(o.length), height: Number(o.height) })),
  })), [surfaces])

  const result = useMemo(() => {
    if (surfacesForCalc.length === 0) return { value: null, error: null }
    try { return { value: calculateSurfaceArea(surfacesForCalc, { wastePct: wastePct === '' ? NaN : Number(wastePct) }), error: null } }
    catch (e) { return { value: null, error: e.message } }
  }, [surfacesForCalc, wastePct])

  async function handleSave() {
    if (!result.value) return
    const engineeringInputs = { surfaces, wastePct: Number(wastePct) }
    const succeeded = await onSave({
      calculatedValueTon: result.value.finalQuantity, unit: 'm2',
      geometrySnapshot: { items: [] }, engineeringInputs,
      breakdownSnapshot: { steps: result.value.steps, surfaceBreakdown: result.value.surfaceBreakdown },
      takeoffItemIdForProvenance: null,
    })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Surfaces</label>
        <Button size="sm" variant="secondary" onClick={addSurface}>+ Add Surface</Button>
      </div>
      {surfaces.map((s) => (
        <div key={s.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Label" value={s.label} onChange={(e) => updateSurface(s.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeSurface(s.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6, marginBottom: 6 }}>
            <LabeledSelect label="Type" value={s.type} onChange={(e) => updateSurface(s.id, { type: e.target.value })}>{TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</LabeledSelect>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, marginTop: 14 }}>
              <input type="checkbox" checked={s.useArea} onChange={(e) => updateSurface(s.id, { useArea: e.target.checked })} /> Use Area directly
            </label>
          </div>
          {s.useArea ? (
            <LabeledInput label="Area (m²)" type="number" value={s.area} onChange={(e) => updateSurface(s.id, { area: e.target.value })} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <LabeledInput label="Length (m)" type="number" value={s.length} onChange={(e) => updateSurface(s.id, { length: e.target.value })} />
              <LabeledInput label="Height (m)" type="number" value={s.height} onChange={(e) => updateSurface(s.id, { height: e.target.value })} />
            </div>
          )}
          {s.type === 'waterproofing' && (
            <>
              <LabeledInput label="Upstand Height (m)" type="number" value={s.upstandHeight} onChange={(e) => updateSurface(s.id, { upstandHeight: e.target.value })} />
              <LabeledInput label="System Specification (optional, informational only — does not affect quantity)" value={s.systemSpecification || ''} onChange={(e) => updateSurface(s.id, { systemSpecification: e.target.value })} />
            </>
          )}
          {s.type === 'painting' && <LabeledInput label="Number of Coats" type="number" value={s.numberOfCoats} onChange={(e) => updateSurface(s.id, { numberOfCoats: e.target.value })} />}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 4px' }}>
            <span style={{ fontSize: 11, fontWeight: 700 }}>Openings (non-overlapping — estimator responsibility)</span>
            <Button size="sm" variant="secondary" onClick={() => addOpening(s.id)}>+ Add Opening</Button>
          </div>
          {s.openings.map((o) => (
            <div key={o.id} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <Input placeholder="Label" value={o.label} onChange={(e) => updateOpening(s.id, o.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
              <Input type="number" placeholder="Length (m)" value={o.length} onChange={(e) => updateOpening(s.id, o.id, { length: e.target.value })} style={{ width: 90, fontSize: 11 }} />
              <Input type="number" placeholder="Height (m)" value={o.height} onChange={(e) => updateOpening(s.id, o.id, { height: e.target.value })} style={{ width: 90, fontSize: 11 }} />
              <button onClick={() => removeOpening(s.id, o.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </div>
      ))}
      <LabeledInput label="Waste % — required" type="number" value={wastePct} onChange={(e) => setWastePct(e.target.value)} />
      {result.value && (
        <div style={{ margin: '12px 0' }}>
          {result.value.surfaceBreakdown.map((sb, i) => (
            <div key={i} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8, background: '#FAFBFC', fontSize: 11 }}>
              <strong>{sb.surface.label}</strong> ({sb.surface.type}): Gross {formatNumber(sb.grossArea)} − Openings {formatNumber(sb.openingsArea)} = Net {formatNumber(sb.netArea)} m²
              {sb.upstandArea > 0 && <div>+ Upstand {formatNumber(sb.upstandArea)} m²</div>}
              <div style={{ fontWeight: 700, marginTop: 4 }}>Final: {formatNumber(sb.finalQuantity)} m²</div>
            </div>
          ))}
          <div style={{ padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8, fontWeight: 700 }}>
            Total: {formatNumber(result.value.finalQuantity)} m²
          </div>
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function LabeledInput({ label, ...p }) { return <div style={{marginBottom:6}}><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label><Input {...p} style={{ fontSize: 11, width: '100%' }} /></div> }
function LabeledSelect({ label, children, ...p }) { return <div><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label><Select {...p} style={{ fontSize: 11, width: '100%' }}>{children}</Select></div> }
